<template>
  <v-container fluid tag="section">
    <div class="floating-shapes">
      <div class="shape shape-1"></div>
      <div class="shape shape-2"></div>
      <div class="shape shape-3"></div>
      <div class="shape shape-4"></div>
    </div>

    <v-row justify="center" align="center">
      <v-col cols="12">
        <v-card class="login-main-card" elevation="24" rounded="xl">
          <div class="login-header-section" :style="headerGradientStyle">
            <div class="logo-section text-center">
              <div class="logo-background mx-auto" :style="logoGradientStyle">
                <lord-icon
                  :src="logoIconSrc"
                  trigger="loop"
                  state="loop-cycle"
                  style="width:80px;height:80px"
                  class="logo-image"
                ></lord-icon>
              </div>
              <div class="login-message mt-4">
                <h1 class="login-title">{{ title }}</h1>
                <p class="login-subtitle mb-0">{{ subtitle }}</p>
              </div>
            </div>

            <div class="login-instructions mt-6">
              <v-row>
                <v-col cols="12" sm="6" class="text-center">
                  <v-chip class="glass-chip ma-1" :style="chipGradientStyle" text-color="white" small>
                    <v-icon left small>mdi-account</v-icon>
                    {{ usernameHint }}
                  </v-chip>
                </v-col>
                <v-col cols="12" sm="6" class="text-center">
                  <v-chip class="glass-chip ma-1" :style="chipGradientStyle" text-color="white" small>
                    <v-icon left small>mdi-lock</v-icon>
                    {{ passwordHint }}
                  </v-chip>
                </v-col>
              </v-row>
              <div class="text-center mt-2" v-if="noticeText">
                <v-chip class="glass-chip-example" color="orange" outlined small>
                  {{ noticeText }}
                </v-chip>
              </div>
            </div>
          </div>

          <v-card-text class="pa-8">
            <div class="text-center login-button-section">
              <v-dialog :value="dialogValue" @input="$emit('update:dialogValue', $event)" persistent max-width="500">
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    class="login-btn"
                    :style="btnGradientStyle"
                    x-large
                    rounded
                    elevation="8"
                    v-bind="attrs"
                    v-on="on"
                    block
                  >
                    <v-icon left size="28">mdi-login-variant</v-icon>
                    เข้าสู่ระบบ
                    <v-icon right size="28">mdi-shield-account</v-icon>
                  </v-btn>
                  <slot name="extra-action"></slot>
                </template>

                <v-card class="login-dialog" rounded="xl">
                  <v-card-title class="dialog-header white--text" :style="dialogHeaderGradientStyle">
                    <div class="dialog-title-content">
                      <div class="dialog-icon-bg">
                        <v-icon size="32" color="white">mdi-shield-account</v-icon>
                      </div>
                      <div class="ml-4">
                        <h3 class="mb-1">{{ title }}</h3>
                        <p class="subtitle mb-0">{{ subtitle }}</p>
                      </div>
                    </div>
                  </v-card-title>

                  <v-card-text class="pa-6">
                    <v-alert v-if="lockoutMessage" type="warning" dense outlined class="mb-4">
                      {{ lockoutMessage }}
                    </v-alert>
                    <slot></slot>
                  </v-card-text>

                  <v-divider></v-divider>

                  <v-card-actions class="pa-4">
                    <v-btn rounded outlined color="grey" @click="$emit('update:dialogValue', false)" class="cancel-btn">
                      <v-icon left>mdi-close</v-icon>
                      ยกเลิก
                    </v-btn>
                    <v-spacer></v-spacer>
                    <v-btn
                      rounded
                      large
                      :style="submitGradientStyle"
                      class="submit-btn"
                      elevation="4"
                      :loading="loading"
                      :disabled="!!lockoutMessage"
                      @click="$emit('submit')"
                    >
                      <v-icon left>mdi-login-variant</v-icon>
                      เข้าสู่ระบบ
                    </v-btn>
                  </v-card-actions>
                </v-card>
              </v-dialog>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-snackbar :value="snackbarValue" @input="$emit('update:snackbarValue', $event)" top class="enhanced-snackbar" :style="snackbarGradientStyle" :timeout="snackbarTimeout">
      <div class="white--text">
        <v-icon color="white" left>mdi-information</v-icon>
        {{ snackbarMsg }}
      </div>
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="$emit('update:snackbarValue', false)" color="white">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>

<script>
/**
 * LoginCardShell.vue — frame ที่ใช้ร่วมกันของ 4 ฟอร์ม login (loginCollege/loginuser/loginuser_gov/loginuserEtc)
 * รวม CSS/animation ที่เคยซ้ำกันเกือบ 600 บรรทัด/ไฟล์ไว้ที่เดียว ส่วนที่ต่างกันตามบทบาท (สี, ข้อความ, ไอคอน)
 * ควบคุมผ่าน props ส่วนฟอร์ม/ฟิลด์จริงอยู่ใน default <slot> ของไฟล์ผู้เรียกใช้
 */
export default {
  name: "LoginCardShell",
  props: {
    title: { type: String, required: true },
    subtitle: { type: String, default: "" },
    usernameHint: { type: String, required: true },
    passwordHint: { type: String, required: true },
    noticeText: { type: String, default: "" },
    logoIconSrc: { type: String, default: "https://cdn.lordicon.com/vvyxyrur.json" },
    themeColorStart: { type: String, required: true },
    themeColorEnd: { type: String, required: true },
    dialogValue: { type: Boolean, default: false },
    snackbarValue: { type: Boolean, default: false },
    snackbarMsg: { type: String, default: "" },
    snackbarTimeout: { type: Number, default: 10000 },
    loading: { type: Boolean, default: false },
    lockoutMessage: { type: String, default: "" }
  },
  computed: {
    gradient() {
      return `linear-gradient(135deg, ${this.themeColorStart} 0%, ${this.themeColorEnd} 100%)`;
    },
    headerGradientStyle() {
      return { background: `linear-gradient(135deg, ${this.themeColorStart}14 0%, ${this.themeColorEnd}14 100%)` };
    },
    logoGradientStyle() {
      return { background: this.gradient, boxShadow: `0 15px 35px ${this.themeColorStart}66` };
    },
    chipGradientStyle() {
      return { background: this.gradient };
    },
    btnGradientStyle() {
      return { background: this.gradient, color: "#fff" };
    },
    dialogHeaderGradientStyle() {
      return { background: this.gradient };
    },
    submitGradientStyle() {
      return { background: this.gradient, color: "#fff" };
    },
    snackbarGradientStyle() {
      return {};
    }
  }
};
</script>

<style scoped>
.floating-shapes {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 0;
}

.shape {
  position: absolute;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.1);
  animation: float 6s ease-in-out infinite;
}

.shape-1 { width: 80px; height: 80px; top: 10%; left: 10%; animation-delay: 0s; }
.shape-2 { width: 120px; height: 120px; top: 70%; right: 10%; animation-delay: 2s; }
.shape-3 { width: 60px; height: 60px; top: 30%; right: 30%; animation-delay: 4s; }
.shape-4 { width: 100px; height: 100px; bottom: 10%; left: 20%; animation-delay: 1s; }

@keyframes float {
  0%, 100% { transform: translateY(0px) rotate(0deg); }
  50% { transform: translateY(-20px) rotate(180deg); }
}

.login-main-card {
  background: rgba(255, 255, 255, 0.95) !important;
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  transition: all 0.3s ease;
  overflow: hidden;
  position: relative;
  z-index: 1;
}

.login-main-card:hover {
  transform: translateY(-5px);
}

.login-header-section {
  padding: 40px 20px 20px;
}

.logo-background {
  width: 120px;
  height: 120px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
}

.logo-background::before {
  content: "";
  position: absolute;
  top: -50%;
  left: -50%;
  width: 200%;
  height: 200%;
  background: linear-gradient(45deg, transparent, rgba(255, 255, 255, 0.2), transparent);
  animation: shimmer 3s linear infinite;
}

.logo-image {
  position: relative;
  z-index: 1;
}

.login-title {
  font-size: 2.2rem;
  font-weight: 700;
  color: #2d3748;
  margin-bottom: 8px;
}

.login-subtitle {
  color: #4a5568;
  font-size: 1.1rem;
}

.glass-chip {
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.3) !important;
}

.glass-chip-example {
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 152, 0, 0.5) !important;
  background: rgba(255, 152, 0, 0.1) !important;
}

.login-btn {
  font-weight: 600;
  text-transform: none;
  font-size: 1.2rem;
  padding: 20px 40px;
  transition: all 0.3s ease;
  border: none !important;
  min-height: 60px;
}

.login-btn:hover {
  transform: translateY(-3px);
}

.login-dialog {
  background: rgba(255, 255, 255, 0.98) !important;
  backdrop-filter: blur(20px);
  overflow: hidden;
}

.dialog-title-content {
  display: flex;
  align-items: center;
  width: 100%;
}

.dialog-icon-bg {
  width: 60px;
  height: 60px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.dialog-header h3 {
  font-size: 1.5rem;
  font-weight: 600;
}

.dialog-header .subtitle {
  font-size: 0.9rem;
  opacity: 0.9;
}

.submit-btn {
  font-weight: 600;
  text-transform: none;
}

.cancel-btn {
  transition: all 0.3s ease;
}

.cancel-btn:hover {
  background: rgba(0, 0, 0, 0.05) !important;
}

@keyframes shimmer {
  0% { transform: translateX(-100%) translateY(-100%) rotate(45deg); }
  100% { transform: translateX(100%) translateY(100%) rotate(45deg); }
}

@media (max-width: 960px) {
  .login-title { font-size: 1.8rem; }
  .login-subtitle { font-size: 1rem; }
  .logo-background { width: 100px; height: 100px; }
  .login-btn { font-size: 1.1rem; padding: 16px 32px; }
  .login-header-section { padding: 30px 15px 15px; }
}

@media (max-width: 600px) {
  .login-title { font-size: 1.6rem; }
  .logo-background { width: 90px; height: 90px; }
  .glass-chip { font-size: 0.8rem; margin: 2px !important; }
  .login-header-section { padding: 20px 10px 10px; }
  .login-btn { font-size: 1rem; min-height: 50px; }
}
</style>
